import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { adminEndpoints } from "@/lib/adminApi";
import { AdminPageHeader, AdminCard } from "@/components/admin/AdminUI";

const EMPLOYMENT_TYPES = ["Full-time", "Part-time", "Contract", "Fixed-term", "Apprenticeship"];

function todayISO() {
    return new Date().toISOString().slice(0, 10);
}
function futureISO(days) {
    const d = new Date();
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
}

const empty = {
    title: "",
    slug: "",
    department: "",
    location: "",
    employment_type: "Full-time",
    salary_range: "",
    description_md: "",
    requirements: "",
    benefits: "",
    deadline: futureISO(60),
    published: true,
    sponsorship_available: false,
};

export default function AdminJobFormPage() {
    const { id } = useParams();
    const isEdit = Boolean(id);
    const navigate = useNavigate();
    const [values, setValues] = useState(empty);
    const [loading, setLoading] = useState(isEdit);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [preview, setPreview] = useState(false);

    useEffect(() => {
        if (!isEdit) return;
        let cancelled = false;
        adminEndpoints
            .job(id)
            .then((j) => {
                if (cancelled) return;
                setValues({
                    title: j.title,
                    slug: j.slug,
                    department: j.department,
                    location: j.location,
                    employment_type: j.employment_type,
                    salary_range: j.salary_range || "",
                    description_md: j.description_md,
                    requirements: (j.requirements || []).join("\n"),
                    benefits: (j.benefits || []).join("\n"),
                    deadline: j.deadline,
                    published: j.published,
                    sponsorship_available: j.sponsorship_available,
                });
            })
            .catch((err) => {
                setError(err?.response?.data?.detail || "Failed to load job");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [id, isEdit]);

    const setField = (name, val) => setValues((s) => ({ ...s, [name]: val }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSubmitting(true);
        const payload = {
            title: values.title.trim(),
            slug: values.slug.trim() || null,
            department: values.department.trim(),
            location: values.location.trim(),
            employment_type: values.employment_type,
            salary_range: values.salary_range.trim() || null,
            description_md: values.description_md.trim(),
            requirements: values.requirements
                .split("\n")
                .map((s) => s.trim())
                .filter(Boolean),
            benefits: values.benefits
                .split("\n")
                .map((s) => s.trim())
                .filter(Boolean),
            deadline: values.deadline,
            published: values.published,
            sponsorship_available: values.sponsorship_available,
        };
        try {
            if (isEdit) {
                await adminEndpoints.updateJob(id, payload);
                toast.success("Job updated");
            } else {
                await adminEndpoints.createJob(payload);
                toast.success("Job created");
            }
            navigate("/admin/jobs");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg =
                typeof detail === "string"
                    ? detail
                    : Array.isArray(detail)
                        ? detail.map((d) => d.msg || JSON.stringify(d)).join(" · ")
                        : "Save failed";
            setError(msg);
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="admin-job-form-page">
            <div className="mb-4">
                <Link
                    to="/admin/jobs"
                    className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-brand-offwhite/70 hover:text-brand-gold"
                    data-testid="admin-job-form-back"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Back to jobs
                </Link>
            </div>
            <AdminPageHeader
                eyebrow={isEdit ? "Edit job" : "New job"}
                title={isEdit ? values.title || "Edit job" : "Publish a new job"}
                description="Description supports markdown. Requirements and benefits accept one bullet per line."
                testId="admin-job-form-header"
            />
            {error && (
                <p className="mb-6 border border-rose-400/40 bg-rose-500/10 px-4 py-3 font-mono text-xs text-rose-200">
                    {error}
                </p>
            )}
            {loading ? (
                <AdminCard>Loading job…</AdminCard>
            ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="admin-job-form">
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <TextField
                            label="Title"
                            value={values.title}
                            onChange={(v) => setField("title", v)}
                            required
                            testId="job-field-title"
                        />
                        <TextField
                            label="Slug (auto-generated if blank)"
                            value={values.slug}
                            onChange={(v) => setField("slug", v)}
                            placeholder="e.g. site-manager-manchester"
                            testId="job-field-slug"
                        />
                        <TextField
                            label="Department"
                            value={values.department}
                            onChange={(v) => setField("department", v)}
                            required
                            testId="job-field-department"
                        />
                        <TextField
                            label="Location"
                            value={values.location}
                            onChange={(v) => setField("location", v)}
                            required
                            testId="job-field-location"
                        />
                        <SelectField
                            label="Employment type"
                            value={values.employment_type}
                            onChange={(v) => setField("employment_type", v)}
                            options={EMPLOYMENT_TYPES}
                            testId="job-field-emp-type"
                        />
                        <TextField
                            label="Salary range"
                            value={values.salary_range}
                            onChange={(v) => setField("salary_range", v)}
                            placeholder="e.g. £58,000 – £72,000 + package"
                            testId="job-field-salary"
                        />
                        <TextField
                            label="Deadline"
                            type="date"
                            value={values.deadline}
                            onChange={(v) => setField("deadline", v)}
                            min={todayISO()}
                            required
                            testId="job-field-deadline"
                        />
                        <div className="flex flex-col gap-3">
                            <label className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.25em] text-brand-offwhite/80">
                                <input
                                    type="checkbox"
                                    checked={values.published}
                                    onChange={(e) => setField("published", e.target.checked)}
                                    className="h-4 w-4 accent-brand-gold"
                                    data-testid="job-field-published"
                                />
                                Published (visible on /careers)
                            </label>
                            <label className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.25em] text-brand-offwhite/80">
                                <input
                                    type="checkbox"
                                    checked={values.sponsorship_available}
                                    onChange={(e) => setField("sponsorship_available", e.target.checked)}
                                    className="h-4 w-4 accent-brand-gold"
                                    data-testid="job-field-sponsorship"
                                />
                                Skilled Worker sponsorship available
                            </label>
                        </div>
                    </div>

                    <TextAreaField
                        label={`Description (markdown) ${preview ? "· preview mode" : ""}`}
                        value={values.description_md}
                        onChange={(v) => setField("description_md", v)}
                        rows={12}
                        required
                        testId="job-field-description"
                        toolbar={
                            <button
                                type="button"
                                onClick={() => setPreview((p) => !p)}
                                className="border border-white/20 px-2 py-1 font-mono text-[9px] uppercase tracking-[0.25em] text-brand-offwhite hover:border-brand-gold hover:text-brand-gold"
                                data-testid="job-field-desc-toggle-preview"
                            >
                                {preview ? "Edit" : "Preview"}
                            </button>
                        }
                        renderPreview={preview}
                    />

                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <TextAreaField
                            label="Requirements (one per line)"
                            value={values.requirements}
                            onChange={(v) => setField("requirements", v)}
                            rows={6}
                            required
                            testId="job-field-requirements"
                        />
                        <TextAreaField
                            label="Benefits (one per line)"
                            value={values.benefits}
                            onChange={(v) => setField("benefits", v)}
                            rows={6}
                            testId="job-field-benefits"
                        />
                    </div>

                    <div className="flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={() => navigate("/admin/jobs")}
                            className="border border-white/20 px-6 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] text-brand-offwhite hover:border-brand-gold hover:text-brand-gold"
                            data-testid="admin-job-form-cancel"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="bg-brand-gold px-6 py-2.5 font-mono text-[10px] uppercase tracking-[0.3em] text-brand-charcoal hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
                            data-testid="admin-job-form-submit"
                        >
                            {submitting ? "Saving…" : isEdit ? "Save changes" : "Publish job"}
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
}

function TextField({ label, value, onChange, testId, ...rest }) {
    return (
        <label className="block">
            <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-brand-offwhite/70">
                {label}
            </span>
            <input
                {...rest}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full border border-white/10 bg-white/[0.03] px-3 py-2 font-body text-sm text-brand-offwhite placeholder:text-brand-offwhite/40 focus:border-brand-gold focus:outline-none"
                data-testid={testId}
            />
        </label>
    );
}

function SelectField({ label, value, onChange, options, testId }) {
    return (
        <label className="block">
            <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-brand-offwhite/70">
                {label}
            </span>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full border border-white/10 bg-white/[0.03] px-3 py-2 font-body text-sm text-brand-offwhite focus:border-brand-gold focus:outline-none"
                data-testid={testId}
            >
                {options.map((o) => (
                    <option key={o} value={o} className="bg-brand-charcoal">
                        {o}
                    </option>
                ))}
            </select>
        </label>
    );
}

function TextAreaField({
    label,
    value,
    onChange,
    rows,
    required,
    testId,
    toolbar,
    renderPreview,
}) {
    return (
        <label className="block">
            <span className="mb-2 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.25em] text-brand-offwhite/70">
                {label}
                {toolbar}
            </span>
            {renderPreview ? (
                <div
                    className="prose prose-invert max-w-none border border-white/10 bg-white/[0.03] p-4"
                    data-testid={`${testId}-preview`}
                >
                    <ReactMarkdown>{value || "*Nothing to preview yet.*"}</ReactMarkdown>
                </div>
            ) : (
                <textarea
                    rows={rows || 6}
                    required={required}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className="w-full border border-white/10 bg-white/[0.03] px-3 py-2 font-mono text-sm text-brand-offwhite placeholder:text-brand-offwhite/40 focus:border-brand-gold focus:outline-none"
                    data-testid={testId}
                />
            )}
        </label>
    );
}
